/**
 * Utility Functions for UI Components
 */
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { DRAMA_THRESHOLDS } from '@dramadetector/shared';

/**
 * Merge Tailwind CSS classes with proper precedence
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get drama category color class
 *
 * Updated (2026-02-03): 4-category system for better UI granularity
 */
export function getDramaCategoryColor(category: 'safe' | 'caution' | 'high_drama' | 'extreme_drama'): string {
  const colors = {
    safe: 'bg-drama-safe text-white',
    caution: 'bg-drama-caution text-white',
    high_drama: 'bg-drama-high text-white',
    extreme_drama: 'bg-drama-extreme text-white',
  };
  return colors[category];
}

/**
 * Get drama score color class based on score value
 *
 * Updated (2026-02-03): 4-category system for better UI granularity
 */
export function getDramaScoreColor(score: number): string {
  if (score <= DRAMA_THRESHOLDS.SAFE_MAX) return 'text-drama-safe';
  if (score <= DRAMA_THRESHOLDS.CAUTION_MAX) return 'text-drama-caution';
  if (score <= DRAMA_THRESHOLDS.HIGH_DRAMA_MAX) return 'text-drama-high';
  return 'text-drama-extreme';
}

/**
 * Format drama score for display
 *
 * Research-backed crisis communication (2026-01-25):
 * - Avoid numeric scores during crisis (0.0 looks like error)
 * - Use qualitative labels instead (human-centered language)
 * - Source: Crisis Text Line, BetterHelp, Headspace UX patterns
 *
 * @param score - Drama score (0-10) or null for crisis detection
 * @returns Formatted score or "Support Available" for crisis
 */
export function formatDramaScore(score: number | null): string {
  if (score === null) {
    return 'Support Available';
  }
  return score.toFixed(1);
}
